// The signed-in CSR's name and first name, in one hook (2026-10-30,
// signedInIdentity.js): /api/me, else MSAL's active account. /api/me is read
// once per page load and shared (App.jsx's own read goes through loadMe()).

import { useEffect, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { fetchMe } from './api.js'
import { signedInName, firstNameOf } from './signedInIdentity.js'

let mePromise = null
let meValue = null
const listeners = new Set()

// /api/me, read once and shared.
export function loadMe() {
  if (!mePromise) {
    mePromise = fetchMe().then((me) => {
      meValue = me ?? null
      for (const fn of listeners) fn(meValue)
      return me
    }).catch((err) => {
      mePromise = null
      throw err
    })
  }
  return mePromise
}

// The e2e build has no MSAL account and every mocked /api/me names a test
// user; its boxes keep signing with no name unless a test opts in
// (window.__CXHUB_E2E_ME_NAME__ = true). Inert in production.
const E2E = import.meta.env?.VITE_E2E === 'true'
function useMeName() {
  if (!E2E) return true
  try { return window.__CXHUB_E2E_ME_NAME__ === true } catch { return false }
}

export function useSignedInIdentity() {
  const { instance } = useMsal()
  const [me, setMe] = useState(meValue)
  useEffect(() => {
    listeners.add(setMe)
    loadMe().catch(() => {})
    return () => { listeners.delete(setMe) }
  }, [])
  let activeAccount = null
  try { activeAccount = instance?.getActiveAccount?.() ?? null } catch { activeAccount = null }
  const name = signedInName({ me, activeAccount, useMe: useMeName() })
  return { name, firstName: firstNameOf(name), username: me?.username ?? activeAccount?.username ?? null }
}
